import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  TruckIcon,
  MapPinIcon,
  MoneyIcon,
  GearIcon,
  AddressBookIcon,
  UsersIcon,
  LeafIcon,
  InfoIcon,
  ArrowsClockwiseIcon,
} from '@phosphor-icons/react';

import { useAuth } from '../../contexts/auth-context';
import { useTranslation } from '../../contexts/translation-context';
import { Sidebar } from '../../components/Sidebar';
import { SidebarNavItem, SidebarUserItem, type ISidebarNavItemProps } from '../../components/sidebar/SidebarNavItem';
import { useCustomerPortalCustomer } from './CustomerPortalCustomerContext';
import { ErrorStateBoundary } from '../../components/error-state/ErrorStateBoundary';

export interface ICustomerPortalDashboardProps {
  children: React.ReactNode;
}

type NavItem = Pick<ISidebarNavItemProps, 'to' | 'label' | 'icon'>;

export const CustomerPortalDashboard: React.FC<ICustomerPortalDashboardProps> = (props) => {
  const { children } = props;
  const { me } = useAuth();
  const { customer } = useCustomerPortalCustomer();
  const { i18n, lang } = useTranslation();

  const [navigationOne, navigationTwo] = useMemo(() => {
    const navigationOne: NavItem[] = [
      { label: i18n('customerPortal.orders.orders'), to: './orders', icon: TruckIcon },
      { label: i18n('customerPortal.locations.locations'), to: './locations', icon: MapPinIcon },
      { label: i18n('customerPortal.quotes.quotes'), to: './quotations', icon: MoneyIcon },
      { label: i18n('customerPortal.invoices.invoices'), to: './invoices', icon: MoneyIcon },
      {
        label: i18n('customerPortal.creditNotes.creditNotes'),
        to: './creditnotes',
        icon: MoneyIcon,
      },
      {
        label: i18n('customerPortal.sustainability.sustainability'),
        to: './sustainability',
        icon: LeafIcon,
      },
    ];

    const navigationTwo: NavItem[] = [
      { label: i18n('customerPortal.contacts.contacts'), to: './contacts', icon: AddressBookIcon },
      { label: i18n('customerPortal.users.users'), to: './users', icon: UsersIcon },
      {
        label: i18n('customerPortal.businessInformation.businessInformation'),
        to: './business-information',
        icon: GearIcon,
      },
      {
        label: i18n('customerPortal.ourInformation.ourInformation'),
        to: './our-information',
        icon: InfoIcon,
      },
      {
        label: i18n('customerPortal.settings.settings'),
        to: './settings',
        icon: GearIcon,
      },
    ];

    return [navigationOne, navigationTwo];
  }, [i18n, lang]);

  return (
    <div className="flex min-h-screen w-full">
      <div className="shrink-0">
        <Sidebar
          homeLink="."
          topItems={
            <>
              {me.customers.length > 1 && (
                <div className="px-3 pb-2">
                  <Link
                    to="../select"
                    className="flex items-center gap-1.5 rounded-md border border-brand-border bg-brand-subtle px-2.5 py-1.5 text-xs font-medium text-brand-text transition-colors hover:bg-warning-subtle"
                  >
                    <span className="flex-1 truncate">{customer.name}</span>
                    <ArrowsClockwiseIcon className="h-3 w-3 shrink-0" />
                  </Link>
                </div>
              )}

              <nav className="sidebar-nav">
                {navigationOne.map((item) => (
                  <SidebarNavItem key={item.to} {...item} />
                ))}
              </nav>

              <div className="sidebar-divider" />

              <nav className="sidebar-nav">
                {navigationTwo.map((item) => (
                  <SidebarNavItem key={item.to} {...item} />
                ))}
              </nav>
            </>
          }
          bottomItems={
            <nav className="sidebar-nav">
              <SidebarUserItem to="me" name={me.name} imageUrl={me.image} meta={customer.name} />
            </nav>
          }
        />
      </div>

      <main className="pb-32 content-bg min-h-screen flex-1 min-w-0">
        <ErrorStateBoundary variant="page">{children}</ErrorStateBoundary>
      </main>
    </div>
  );
};
